import { useEffect,useMemo,useState } from 'react'
import { listEvents,listEventModules } from '../event-builder/eventRepository'
import type { EventRecord } from '../event-builder/types'
import { cancelManagedOrder,captureCustomerCashPayment,listManagedOrders,reprintManagedOrder } from './orderManagementRepository'
import { eventToday } from './orderHistoryDate'
import type { ManagedOrder } from './types'

interface Props{organizationId:string;canCancel:boolean;canCapturePayment:boolean;canReprint:boolean;onBack:()=>void}
const eur=(c:number)=>new Intl.NumberFormat('it-IT',{style:'currency',currency:'EUR'}).format(c/100)
const dt=(v:string)=>new Intl.DateTimeFormat('it-IT',{dateStyle:'short',timeStyle:'short',timeZone:'Europe/Rome'}).format(new Date(v))
const tableLabel=(v:string)=>`Tavolo ${v.replace(/^tavolo\s*/i,'')}`
const stateLabel=(v:string)=>v==='confirmed'?'Confermato':v==='cancelled'?'Annullato':'In attesa'
function payLabel(o:ManagedOrder){
 const provider=(o.paymentProvider??'').toLowerCase()
 const method=(o.paymentMethod??'').toLowerCase()
 const payment=provider.includes('nexi')?'Nexi POS':
  provider.includes('sumup')?'SumUp':
  method==='cash'||provider==='cash'?'Contanti':
  method==='tap'||method==='card'||method==='contactless'?'Carta / Contactless':
  method==='online'?'Online':method?o.paymentMethod??'Altro':'Non specificato'
 const status=o.paymentStatus==='paid'?'Pagato':o.paymentStatus==='cancelled'?'Annullato':
   o.paymentStatus==='pending'?'Da verificare':o.paymentStatus??'Sconosciuto'
 return `${payment} · ${status}`
}
export function Orders({organizationId,canCancel,canCapturePayment,canReprint,onBack}:Props){
 const [events,setEvents]=useState<EventRecord[]>([])
 const [eventId,setEventId]=useState('')
 const [day,setDay]=useState(eventToday)
 const [allDays,setAllDays]=useState(false)
 const [orders,setOrders]=useState<ManagedOrder[]>([])
 const [query,setQuery]=useState('')
 const [expanded,setExpanded]=useState<string|null>(null)
 const [cancelling,setCancelling]=useState<ManagedOrder|null>(null)
 const [reason,setReason]=useState('')
 const [loading,setLoading]=useState(true)
 const [busy,setBusy]=useState(false)
 const [error,setError]=useState<string|null>(null)
 const [notice,setNotice]=useState<string|null>(null)
 const [reload,setReload]=useState(0)
 useEffect(()=>{
  setLoading(true)
  listEvents(organizationId).then(async rows=>{
   const checks=await Promise.all(rows.map(async event=>({event,modules:await listEventModules(event.id)})))
   const enabled=checks.filter(x=>x.modules.some(m=>m.moduleId==='orders'&&m.enabled)).map(x=>x.event)
   setEvents(enabled)
   setEventId(c=>enabled.some(e=>e.id===c)?c:enabled[0]?.id??'')
  }).catch(e=>setError(e instanceof Error?e.message:'Errore caricamento eventi'))
   .finally(()=>setLoading(false))
 },[organizationId])
 useEffect(()=>{
  if(!eventId){setOrders([]);return}
  let live=true
  setLoading(true);setError(null);setNotice(null)
  listManagedOrders(eventId,allDays?null:day).then(rows=>{
   if(live)setOrders(rows)
  }).catch(e=>{if(live)setError(e instanceof Error?e.message:'Errore caricamento ordini')})
   .finally(()=>{if(live)setLoading(false)})
  return()=>{live=false}
 },[eventId,day,allDays,reload])
 const refresh=()=>setReload(n=>n+1)
 const filtered=useMemo(()=>{
  const needle=query.trim().toLowerCase()
  return orders.filter(o=>!needle||String(o.number).includes(needle)||
    (o.customerName??'').toLowerCase().includes(needle)||
    (o.serviceLocation??'').toLowerCase().includes(needle)||
    payLabel(o).toLowerCase().includes(needle)||
    o.lines.some(l=>l.productName.toLowerCase().includes(needle)))
 },[orders,query])
 async function collectCash(o:ManagedOrder){
  if(!window.confirm(`Confermi ${eur(o.totalCents)} in contanti per #${o.number} · ${o.customerName??''} · ${o.serviceLocation??''}?`))return
  setBusy(true);setError(null)
  try{await captureCustomerCashPayment(o.id);refresh()}
  catch(e){setError(e instanceof Error?e.message:'Errore conferma pagamento')}
  finally{setBusy(false)}
 }
 async function confirmCancel(){
  if(!cancelling)return
  setBusy(true);setError(null)
  try{await cancelManagedOrder(cancelling.id,reason);setCancelling(null);setReason('');refresh()}
  catch(e){setError(e instanceof Error?e.message:'Errore annullamento')}
  finally{setBusy(false)}
 }
 async function reprint(o:ManagedOrder,scope:'cashier'|'all'){
  const what=scope==='cashier'?'il solo scontrino CASSA':'TUTTI gli scontrini, compresi quelli di cucina e reparti'
  const warning=scope==='all'?'\nATTENZIONE: questi fogli sono solo copie, NON devono generare nuova preparazione.':''
  if(!window.confirm(`Ristampare ${what} dell’ordine #${o.number}?${warning}\nNessun nuovo pagamento e nessun nuovo invio in produzione.`))return
  setBusy(true);setError(null);setNotice(null)
  try{
   const count=await reprintManagedOrder(o.id,scope)
   setNotice(`Ristampa ordine #${o.number} inviata alla coda: ${count} scontrin${count===1?'o':'i'}.`)
  }catch(e){setError(e instanceof Error?e.message:'Ristampa non riuscita')}
  finally{setBusy(false)}
 }
 const activeEvent=events.find(e=>e.id===eventId)
 return <section className="orders-module section">
  <div className="section-heading orders-heading"><div><p className="eyebrow">STORICO ORDINI</p><h1>{activeEvent?.name??'Gestione ordini'}</h1></div><button className="secondary" onClick={onBack}>← Centro evento</button></div>
  {error&&<div className="alert">{error}</div>}
  {notice&&<div className="orders-notice" role="status">{notice}</div>}
  <div className="orders-toolbar">
   <label className="orders-date-field"><span>Giorno dell’evento</span><input type="date" value={day} disabled={allDays} onChange={e=>{setDay(e.target.value);setExpanded(null)}}/></label>
   <label className="orders-all-days"><input type="checkbox" checked={allDays} onChange={e=>{setAllDays(e.target.checked);setExpanded(null)}}/> Tutti i giorni</label>
   <input value={query} onChange={e=>setQuery(e.target.value)} placeholder="Cerca #, nome, pagamento o prodotto" aria-label="Cerca negli ordini"/>
   <button className="secondary" onClick={refresh} disabled={loading}>Aggiorna</button>
  </div>
  <p className="orders-result-count">{loading?'Caricamento…':`${filtered.length} ordini ${allDays?'complessivi':`del ${day.split('-').reverse().join('/')}`}`}</p>
  {!loading&&filtered.length===0&&<p className="muted">Nessun ordine per la data e i filtri selezionati.</p>}
  {!loading&&<div className="orders-list">{filtered.map(o=><article className={`order-card ${o.status==='cancelled'?'cancelled':''}`} key={o.id}>
   <button className="order-summary" onClick={()=>setExpanded(expanded===o.id?null:o.id)} aria-expanded={expanded===o.id}>
    <span className="orders-summary-info">
     <strong>#{o.number} {o.customerName&&`· ${o.customerName}`}</strong>
     <small>{o.serviceLocation?`${tableLabel(o.serviceLocation)} · ${dt(o.createdAt)}`:dt(o.createdAt)}</small>
     <small className="orders-payment-label">{(o.paymentMethod?.toLowerCase()==='cash'||o.paymentProvider?.toLowerCase()==='cash')?'💶':'💳'} {payLabel(o)}</small>
    </span>
    <span className={`order-status ${o.status}`}>{stateLabel(o.status)}</span>
    <strong className="orders-sum">{eur(o.totalCents)}</strong>
   </button>
   {expanded===o.id&&<div className="order-detail">
    {o.serviceLocation&&<h2>📍 {tableLabel(o.serviceLocation)} · {o.customerName}</h2>}
    {o.lines.map(l=><div className="order-line" key={l.id}><div><strong>{l.quantity} × {l.productName}</strong>{l.variants.length>0&&<small>{l.variants.join(' · ')}</small>}{l.note&&<small style={{display:'block',whiteSpace:'pre-wrap',color:'#ad480e',fontWeight:750}}>NOTA: {l.note}</small>}</div><span>{eur(l.lineTotalCents)}</span></div>)}
    <div className="order-meta">
     <span>Origine: {o.source}</span>
     <span>Pagamento: {payLabel(o)}</span>
     {o.status==='cancelled'&&o.paymentStatus==='paid'&&<strong className="orders-payment-warning">Ordine annullato ma pagamento registrato: verificare eventuale rimborso.</strong>}
    </div>
    {canReprint&&o.status==='confirmed'&&<div className="orders-reprint-actions">
     <button className="secondary" disabled={busy} onClick={()=>void reprint(o,'cashier')}>🖨 Ristampa CASSA</button>
     <button className="secondary" disabled={busy} onClick={()=>void reprint(o,'all')}>🖨 Ristampa tutti gli scontrini</button>
    </div>}
    {canCapturePayment&&o.status==='pending_payment'&&o.paymentMethod==='cash'&&<button onClick={()=>void collectCash(o)} disabled={busy}>💶 Pagamento ricevuto · conferma ordine</button>}
    {canCancel&&o.status!=='cancelled'&&<button className="danger" onClick={()=>{setCancelling(o);setReason('')}}>Annulla ordine</button>}
   </div>}
  </article>)}</div>}
  {cancelling&&<div className="order-modal-backdrop"><div className="order-modal panel"><h2>Annulla #{cancelling.number}</h2><textarea value={reason} onChange={e=>setReason(e.target.value)} rows={3}/><div className="order-modal-actions"><button onClick={()=>setCancelling(null)}>Chiudi</button><button className="danger" onClick={()=>void confirmCancel()} disabled={busy}>Conferma annullamento</button></div></div></div>}
 </section>
}
